<template>
  <div class="container-fluid mt-4">
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status"><span class="visually-hidden">{{ $t('product.loading') }}</span></div>
    </div>
    <div v-else-if="error" class="alert alert-danger">{{ $t('product.failedToFetchProducts') }}</div>
    
    <div v-else-if="product">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h2">{{ product.name }}</h1>
        <router-link to="/products" class="btn btn-sm btn-outline-secondary">
          <i class="fas fa-arrow-left me-1"></i> {{ $t('product.backToProducts') }}
        </router-link>
      </div>

      <div class="row">
        <!-- Product Information -->
        <div class="col-lg-5 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-info-circle me-2"></i> {{ $t('product.productInformation') }}</h5>
            </div>
            <div class="card-body">
              <p><strong>{{ $t('product.category') }}:</strong> <span class="badge bg-info">{{ product.category }}</span></p>
              <p><strong>{{ $t('product.brand') }}:</strong> {{ product.brand }}</p>
              <p><strong>{{ $t('product.model') }}:</strong> {{ product.model || 'N/A' }}</p>
              
              <!-- Purchase Count Information -->
              <div class="mb-3">
                <strong>{{ $t('product.monthlyPurchases') }}:</strong>
                <span v-if="product.bought_past_month" class="text-success">
                  {{ formatNumber(product.bought_past_month) }} {{ $t('product.purchases') }}
                </span>
                <span v-else class="text-muted">{{ $t('product.noPurchaseData') }}</span>
              </div>
              <div v-if="product.bought_past_month_updated_at" class="mb-3">
                <strong>{{ $t('product.dataUpdatedAt') }}:</strong>
                <span class="text-muted small">{{ formatDate(product.bought_past_month_updated_at) }}</span>
              </div>
              <p><strong>{{ $t('product.specifications') }}:</strong></p>
              <div class="p-3 bg-light rounded">
                <pre>{{ product.specifications }}</pre>
              </div>
            </div>
          </div>
        </div>

        <!-- Price & Availability -->
        <div class="col-lg-7 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-dollar-sign me-2"></i> {{ $t('product.priceAndAvailability') }}</h5>
            </div>
            <div class="card-body">
              <div v-if="priceInfo">
                <p><strong>{{ $t('product.currentPrice') }}:</strong> <span class="fs-4 fw-bold text-primary">{{ priceInfo.currency }} {{ formatPrice(priceInfo.current_price) }}</span></p>
                <p v-if="priceInfo.original_price && priceInfo.original_price > priceInfo.current_price">
                  <strong>{{ $t('product.originalPrice') }}:</strong> <span class="text-muted text-decoration-line-through">{{ priceInfo.currency }} {{ formatPrice(priceInfo.original_price) }}</span>
                </p>
                <p v-if="priceInfo.discount_percentage">
                  <strong>{{ $t('product.discount') }}:</strong> <span class="badge bg-warning">{{ priceInfo.discount_percentage.toFixed(1) }}% {{ $t('product.off') }}</span>
                </p>
                <p><strong>{{ $t('product.availability') }}:</strong> 
                  <span :class="['badge', priceInfo.availability && priceInfo.availability.includes('In Stock') ? 'bg-success' : 'bg-danger']">
                    {{ priceInfo.availability || 'N/A' }}
                  </span>
                </p>
                <p><strong>{{ $t('product.lastUpdated') }}:</strong> {{ priceInfo.last_updated ? formatDate(priceInfo.last_updated) : 'N/A' }}</p>
              </div>
              <div v-else class="text-muted">
                {{ $t('product.noCurrentPriceDataAvailable') }}
              </div>
              <!-- Moved outside v-if="priceInfo" to always show -->
              <p>
                <strong>{{ $t('product.bspPrice') }}:</strong> 
                <span class="fs-4 fw-bold text-success me-2">{{ priceInfo?.currency || '' }} {{ formatPrice(product.bsp_price) }}</span>
                <button class="btn btn-sm btn-outline-primary" @click="openBspPriceModal">
                  <i class="fas fa-edit"></i>
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Price History Chart -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-chart-area me-2"></i> {{ $t('product.priceHistory') }}</h5>
            </div>
            <div class="card-body">
              <PriceHistoryChart 
                v-if="product.id"
                :product-id="product.id"
                :auto-load="true"
                @data-loaded="onPriceHistoryLoaded"
                @error="onPriceHistoryError"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Product Variations -->
      <div class="row mt-4" v-if="variations && variations.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-boxes me-2"></i> {{ $t('product.productVariations') }}</h5>
            </div>
            <div class="card-body">
              <ul class="list-group list-group-flush">
                <li v-for="variation in variations" :key="variation.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6>{{ variation.variation_name }}</h6>
                    <span class="fw-bold text-info">{{ variation.currency }} {{ formatPrice(variation.price) }}</span>
                  </div>
                  <p class="text-muted small mb-1">{{ $t('product.asin') }}: {{ variation.asin }}</p>
                  <p v-if="variation.color" class="text-muted small mb-1">{{ $t('product.color') }}: {{ variation.color }}</p>
                  <p v-if="variation.size" class="text-muted small mb-1">{{ $t('product.size') }}: {{ variation.size }}</p>
                  <img v-if="variation.image_url" :src="variation.image_url" class="img-thumbnail mt-2" style="max-width: 100px;" :alt="variation.variation_name">
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Reviews -->
      <div class="row mt-4" v-if="recentReviews && recentReviews.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-star me-2"></i> {{ $t('product.recentReviews') }}</h5>
            </div>
            <div class="card-body">
              <ul class="list-group list-group-flush">
                <li v-for="review in recentReviews" :key="review.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <strong>{{ review.source_platform }}</strong>
                    <small class="text-muted">{{ formatDate(review.recorded_at) }}</small>
                  </div>
                  <p class="mb-1">{{ $t('product.rating') }}: {{ review.rating }}/5</p>
                  <p class="text-muted small">{{ review.top_review_content }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Brand Sentiment -->
      <div class="row mt-4" v-if="brandSentiment && brandSentiment.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h5 class="card-title mb-0"><i class="fas fa-heart me-2"></i> {{ $t('product.brandSentiment') }}</h5>
            </div>
            <div class="card-body">
              <ul class="list-group list-group-flush">
                <li v-for="sentiment in brandSentiment" :key="sentiment.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <strong>{{ sentiment.source_platform }}</strong>
                    <span :class="['badge', {
                      'bg-success': sentiment.sentiment_category === $t('product.positive'),
                      'bg-danger': sentiment.sentiment_category === $t('product.negative'),
                      'bg-secondary': sentiment.sentiment_category === $t('product.neutral')
                    }]">{{ sentiment.sentiment_category }}</span>
                  </div>
                  <p class="mb-1">{{ $t('product.score') }}: {{ sentiment.sentiment_score }}</p>
                  <p class="text-muted small">{{ $t('product.keywords') }}: {{ sentiment.keywords || 'N/A' }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reusable BSP Price Edit Modal -->
    <BspPriceEditModal 
      :product="product" 
      @bspPriceUpdated="handleBspPriceUpdated" 
      ref="bspPriceModalRef" 
      v-if="product"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import apiClient from '../api';
import { useRoute } from 'vue-router';
import PriceHistoryChart from './PriceHistoryChart.vue';
import { formatDate, formatNumber } from '../utils/formatters';

const product = ref(null);
const priceInfo = ref(null);
const variations = ref([]);
const priceHistory = ref([]);
const recentReviews = ref([]);
const brandSentiment = ref([]);
const loading = ref(true);
const error = ref(null);
const route = useRoute();

const bspPriceModalRef = ref(null); // Ref for the BspPriceEditModal component

const formatPrice = (price) => price ? price.toFixed(2) : 'N/A';

const fetchProductDetail = async () => {
  try {
    const response = await apiClient.get(`/products/${route.params.id}`);
    console.log("Product Detail API Response:", response.data);
    product.value = response.data.product;
    priceInfo.value = response.data.price_info;
    variations.value = response.data.variations;
    priceHistory.value = response.data.price_history;
    recentReviews.value = response.data.recent_reviews;
    brandSentiment.value = response.data.brand_sentiment;
  } catch (err) {
    error.value = 'Failed to fetch product details.';
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const openBspPriceModal = () => {
  if (bspPriceModalRef.value) {
    bspPriceModalRef.value.openModal();
  }
};

const handleBspPriceUpdated = ({ productId, newBspPrice }) => {
  if (product.value && product.value.id === productId) {
    product.value.bsp_price = newBspPrice; // Update UI immediately
  }
  // Optionally, show a success message
};

// 價格歷史記錄相關事件處理
const onPriceHistoryLoaded = (data) => {
  console.log('Price history loaded for product detail:', data);
};

const onPriceHistoryError = (error) => {
  console.error('Price history error in product detail:', error);
};

onMounted(() => {
  fetchProductDetail();

  // Check for query parameter on initial load
  if (route.query.openBspModal === 'true') {
    // Ensure product data is loaded before opening modal
    const unwatchProduct = watch(product, (newProduct) => {
      if (newProduct) {
        openBspPriceModal();
        unwatchProduct(); // Stop watching once modal is opened
      }
    }, { immediate: true }); // Run immediately if product is already loaded
  }
});

// Watch for route query changes
watch(() => route.query.openBspModal, (newVal) => {
  if (newVal === 'true' && product.value) { // Ensure product data is loaded
    openBspPriceModal();
  }
});
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
